import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { H2, P, UL, CONTACT_EMAIL, COMPANY_LEGAL_NAME } from './LegalPage';

// Public terms for the Services. Conventional B2B SaaS structure, category-
// level wording, no product walk-throughs (owner 2026-10-07). Section 14 is
// the mobile-messaging program terms carriers expect to find for SMS
// registration (program description, frequency, rates, STOP, HELP, carrier
// liability, privacy link).
// 2026-10-10: Section 4 extended after the PIPEDA / Alberta PIPA review
// (service-provider authorization and responsibility, data location, security
// and confidentiality covenants, incident notice, access-request assistance,
// deletion commitment, the Customer's own privacy duties). Kept inside
// Section 4 so the survival clause in Section 11 stays correct.
const EFFECTIVE = 'October 10, 2026';

const TermsOfService = () => (
  <LegalPage title="Terms & Conditions" effective={EFFECTIVE}>
    <P>
      These Terms &amp; Conditions (the &ldquo;Terms&rdquo;) govern access to and use of the websites,
      web and mobile applications and related services (collectively, the &ldquo;Services&rdquo;) made
      available by {COMPANY_LEGAL_NAME} (&ldquo;Simplicity Dynamics&rdquo;, &ldquo;we&rdquo;,
      &ldquo;us&rdquo; or &ldquo;our&rdquo;). By accessing or using the Services you agree to be bound by
      these Terms. If you are using the Services on behalf of an organization, you represent that you
      have authority to bind that organization, and &ldquo;Customer&rdquo; or &ldquo;you&rdquo; refers
      to that organization. If you do not agree to these Terms, do not use the Services.
    </P>

    <H2>1. Definitions</H2>
    <UL
      items={[
        '“Customer Data” means all data, content and materials submitted to the Services by or on behalf of the Customer, including information about the Customer’s personnel.',
        '“Authorized Users” means the Customer’s employees, contractors and agents whom the Customer permits to access the Services.',
        '“Order” means an order form, quotation or online plan selection that specifies the Services purchased, the subscription term and the fees.',
        '“Documentation” means the user guides and help materials we make available for the Services.',
      ]}
    />

    <H2>2. Accounts and Access</H2>
    <UL
      items={[
        'The Customer is responsible for all activity under its account and the accounts of its Authorized Users, for maintaining the confidentiality of credentials, and for configuring access permissions appropriately.',
        'The Customer shall promptly notify us of any unauthorized use of an account or any other breach of security.',
        'Access credentials and activation links are personal to the Authorized User to whom they are issued and may not be shared.',
      ]}
    />

    <H2>3. Subscription, Fees and Payment</H2>
    <UL
      items={[
        'The Services are provided on a subscription basis for the term and at the fees set out in the applicable Order. Unless otherwise stated, fees are quoted in Canadian dollars, are exclusive of applicable taxes (including GST/HST), and are payable in advance.',
        'Any trial or early-access period is provided at our discretion, and we will give the Customer reasonable advance notice before fees become payable.',
        'We may change fees upon at least thirty (30) days’ notice, effective at the start of the next subscription period.',
        'We may suspend access to the Services for non-payment after notice, and will restore access upon receipt of amounts due.',
      ]}
    />

    <H2>4. Customer Data</H2>
    <UL
      items={[
        'As between the parties, the Customer owns all right, title and interest in Customer Data. The Customer grants us a limited, non-exclusive licence to host, copy, process, transmit and display Customer Data solely as necessary to provide and support the Services and as described in our Privacy Policy. Where this Section 4 and the Privacy Policy differ with respect to personal information in Customer Data, this Section 4 prevails.',
        'The Customer is solely responsible for the accuracy, quality and lawfulness of Customer Data and for the means by which it is acquired. The Customer shall collect, use and disclose personal information through the Services only for purposes that are reasonable and permitted by applicable privacy law (including, for information about its personnel, purposes reasonably required to establish, manage or terminate its relationship with them), shall provide any notices to, and obtain any consents from, its personnel required by applicable privacy, employment and electronic-monitoring laws before collection begins, and represents that it is entitled to instruct us to process that information.',
        'We process personal information contained in Customer Data on the Customer’s behalf and under its instructions, and we will not use it for any purpose other than providing the Services. We permit access to it only to personnel and service providers who need it to provide the Services and who are bound by appropriate confidentiality obligations. Customer Data is the Customer’s confidential information for the purposes of Section 10.',
        'The Customer authorizes us to engage service providers, including cloud hosting, communications and other providers that may be located in the United States or other countries outside Canada, to process Customer Data in order to provide the Services. Customer Data may therefore be stored and processed outside Canada and may be accessible to authorities in those jurisdictions under their laws. We may change our service providers from time to time, require them by contract to protect Customer Data at a level comparable to these Terms, and remain responsible to the Customer for their handling of Customer Data under these Terms.',
        'We maintain administrative, technical and physical safeguards appropriate to the sensitivity of the personal information in Customer Data, as described in our Privacy Policy.',
        'If we become aware of unauthorized access to, or loss or disclosure of, personal information in Customer Data, we will notify the Customer without undue delay and provide the information reasonably available to us about the nature of the incident, the information and individuals affected and the steps taken, and we will reasonably cooperate so that the Customer can meet its notification and record-keeping obligations under applicable privacy law. The Customer is responsible for determining whether notice to regulators or individuals is required.',
        'If we receive a request from an individual for access to or correction of personal information in Customer Data, we may refer the individual to the Customer and will reasonably assist the Customer in responding.',
        'The Customer may export its Customer Data through the functionality of the Services during the subscription term and for thirty (30) days thereafter. After that period we will delete or de-identify Customer Data within a reasonable time, subject to our legal retention obligations; copies in backups are overwritten in the ordinary course.',
      ]}
    />

    <H2>5. Acceptable Use</H2>
    <P>The Customer shall not, and shall not permit any Authorized User to:</P>
    <UL
      items={[
        'use the Services in violation of applicable law or the rights of any person;',
        'upload or transmit content that is unlawful, infringing, defamatory, harassing or malicious, or that contains viruses or other harmful code;',
        'attempt to gain unauthorized access to the Services, to any other customer’s data, or to systems or networks connected to the Services;',
        'interfere with or disrupt the integrity or performance of the Services, or probe, scan or test their vulnerability without our written consent;',
        'copy, modify, reverse engineer, decompile or create derivative works of the Services, or resell, sublicense or otherwise make the Services available to third parties;',
        'use the communication features of the Services to send unsolicited or marketing messages.',
      ]}
    />

    <H2>6. Nature of the Services</H2>
    <UL
      items={[
        'The Services are tools for recording, organizing and communicating business information. They do not constitute legal, accounting, payroll, tax, engineering or safety advice, and they do not relieve the Customer of its obligations under applicable law or its contracts.',
        'Figures, reports and documents generated by the Services are based on data entered by users and on information received from devices, networks and third-party services. The Customer is responsible for reviewing and verifying them before relying on them.',
        'Location-based features depend on mobile devices, telematics hardware, wireless networks and positioning systems that we do not control. Location data may be delayed, incomplete or inaccurate.',
        'Features that use artificial intelligence generate suggestions that may contain errors and must be reviewed by the Customer.',
        'Where the Services operate offline, data is stored on the device until connectivity is restored. We are not responsible for data lost from a device before it has been synchronized.',
      ]}
    />

    <H2>7. Availability, Maintenance and Support</H2>
    <P>
      We will use commercially reasonable efforts to make the Services available at all times, except for
      planned maintenance, emergency maintenance and causes beyond our reasonable control. We maintain
      regular backups of the Services. Support is provided by email at{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-[#2f66b8] hover:underline">
        {CONTACT_EMAIL}
      </a>{' '}
      and through the support features of the Services during our normal business hours.
    </P>

    <H2>8. Third-Party Services</H2>
    <P>
      The Services rely on third-party hosting, communications and other service providers, and may
      interoperate with third-party platforms that the Customer elects to connect. Use of any third-party
      platform is subject to that third party&rsquo;s terms, and we are not responsible for the
      availability, security or conduct of third-party platforms or services that the Customer elects to
      use. This does not limit our responsibility for the service providers we engage, as described in
      Section 4.
    </P>

    <H2>9. Intellectual Property</H2>
    <P>
      The Services, the Documentation and all related software, designs, trademarks and content are owned
      by Simplicity Dynamics or its licensors and are protected by intellectual-property laws. Subject to
      these Terms, we grant the Customer a limited, non-exclusive, non-transferable right to access and use
      the Services during the subscription term for its internal business purposes. All rights not
      expressly granted are reserved. We may use feedback and suggestions provided by the Customer without
      restriction or obligation.
    </P>

    <H2>10. Confidentiality</H2>
    <P>
      Each party shall protect the other party&rsquo;s confidential information using at least the same
      degree of care it uses for its own confidential information, and no less than reasonable care, and
      shall use such information only for the purposes of these Terms. Confidential information does not
      include information that is or becomes publicly available without breach, was known to the receiving
      party without restriction, is independently developed, or is rightfully received from a third party.
      A party may disclose confidential information where required by law, after giving reasonable notice
      where permitted.
    </P>

    <H2>11. Term, Suspension and Termination</H2>
    <UL
      items={[
        'These Terms apply for as long as the Customer maintains an account. Subscriptions continue for the term stated in the Order and renew in accordance with it unless either party gives notice of non-renewal.',
        'The Customer may terminate its subscription in accordance with its Order; fees paid are non-refundable except as required by law or expressly provided in the Order.',
        'We may suspend or terminate the Services upon notice if the Customer materially breaches these Terms and fails to cure the breach within thirty (30) days, fails to pay fees when due, or if continued provision of the Services would be unlawful.',
        'Upon termination, the Customer’s right to access the Services ends. Customer Data remains available for export for thirty (30) days, after which we will delete or de-identify it as set out in Section 4. Sections 4, 9, 10, 12, 13, 15 and 16 survive termination.',
      ]}
    />

    <H2>12. Disclaimer of Warranties</H2>
    <P>
      Except as expressly set out in these Terms, the Services are provided &ldquo;as is&rdquo; and
      &ldquo;as available&rdquo;. To the maximum extent permitted by law, we disclaim all warranties,
      conditions and representations, express, implied or statutory, including implied warranties of
      merchantability, fitness for a particular purpose, title and non-infringement, and any warranty that
      the Services will be uninterrupted, error-free or completely secure.
    </P>

    <H2>13. Limitation of Liability</H2>
    <UL
      items={[
        'To the maximum extent permitted by law, neither party shall be liable for any indirect, incidental, special, consequential or punitive damages, or for loss of profits, revenue, business, goodwill or data, arising out of or in connection with these Terms, however caused and under any theory of liability, even if advised of the possibility of such damages.',
        'To the maximum extent permitted by law, our aggregate liability arising out of or in connection with these Terms and the Services shall not exceed the fees paid by the Customer to us for the Services in the twelve (12) months preceding the event giving rise to the claim, or five hundred Canadian dollars (CAD 500) if no fees were paid.',
        'These limitations do not apply to liability that cannot be limited under applicable law, including liability for fraud or wilful misconduct.',
      ]}
    />

    <H2>14. Mobile Messaging Terms</H2>
    <P>
      Where a Customer enables text-message notifications, {COMPANY_LEGAL_NAME} operates an
      informational messaging program that delivers account, operational and alert notifications to the
      mobile numbers provided by the Customer or its Authorized Users. By providing a mobile number and
      enabling such notifications, the recipient agrees to receive text messages from{' '}
      {COMPANY_LEGAL_NAME} at that number, sent using an automated system. Consent is not a condition of
      purchase. Message frequency varies. Message and data rates may apply; contact your wireless provider
      for details about your plan. Recipients may opt out at any time by replying STOP to any message,
      after which a single confirmation message is sent, or by asking their account administrator to
      disable the notification. Reply HELP for help. For additional assistance, contact{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-[#2f66b8] hover:underline">
        {CONTACT_EMAIL}
      </a>
      . Carriers are not liable for any delayed or undelivered messages. Information about how mobile
      numbers and consent are handled is set out in our{' '}
      <Link to="/privacy" className="font-medium text-[#2f66b8] hover:underline">
        Privacy Policy
      </Link>
      .
    </P>

    <H2>15. Indemnification</H2>
    <P>
      The Customer shall defend, indemnify and hold harmless Simplicity Dynamics and its officers,
      directors, employees and agents from and against any third-party claims, damages, liabilities and
      expenses (including reasonable legal fees) arising out of Customer Data, the Customer&rsquo;s use of
      the Services in breach of these Terms or applicable law, or the Customer&rsquo;s failure to provide
      notices to or obtain consents from its personnel as required by law.
    </P>

    <H2>16. Governing Law and Disputes</H2>
    <P>
      These Terms are governed by the laws of the Province of Alberta and the federal laws of Canada
      applicable therein, without regard to conflict-of-laws principles. The parties attorn to the
      exclusive jurisdiction of the courts of Alberta sitting in Calgary, provided that either party may
      seek injunctive relief in any court of competent jurisdiction.
    </P>

    <H2>17. Changes to the Services and to these Terms</H2>
    <P>
      We may modify the Services from time to time, including by adding, changing or removing features.
      We may also revise these Terms; the revised Terms will be posted on this page with a new effective
      date, and we will provide at least thirty (30) days&rsquo; notice of material changes by email or
      through the Services. Continued use of the Services after the effective date constitutes acceptance
      of the revised Terms.
    </P>

    <H2>18. General</H2>
    <UL
      items={[
        'Entire agreement: these Terms, together with the applicable Order and our Privacy Policy, constitute the entire agreement between the parties regarding the Services and supersede all prior agreements and understandings.',
        'Assignment: the Customer may not assign these Terms without our prior written consent. We may assign these Terms to an affiliate or to a successor in connection with a merger, acquisition or sale of assets.',
        'Force majeure: neither party is liable for delay or failure to perform caused by events beyond its reasonable control.',
        'Severability and waiver: if any provision is held unenforceable, the remaining provisions remain in effect. A failure to enforce a provision is not a waiver of it.',
        'Notices: notices to us shall be sent to the email address below; notices to the Customer may be sent to the email address associated with its account.',
      ]}
    />

    <H2>19. Contact</H2>
    <P>
      {COMPANY_LEGAL_NAME}, Calgary, Alberta, Canada. Email:{' '}
      <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-[#2f66b8] hover:underline">
        {CONTACT_EMAIL}
      </a>
      .
    </P>
  </LegalPage>
);

export default TermsOfService;
